import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Dumbbell, Target, Apple, TrendingUp, Sparkles, Heart, Zap, Menu, X } from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const features = [
    {
      icon: <Target className="w-8 h-8" />,
      title: "Personalisierte Trainingspläne",
      description: "Maßgeschneiderte Workouts basierend auf deinen Zielen und Fitness-Level"
    },
    {
      icon: <Apple className="w-8 h-8" />,
      title: "Individuelle Ernährung",
      description: "Mahlzeitenpläne angepasst an deine Präferenzen und Ernährungsziele"
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Fortschrittsverfolgung",
      description: "Verfolge deine Transformation mit detaillierten Statistiken"
    }
  ];

  const testimonials = [
    {
      name: "Sarah M.",
      text: "Diese Plattform hat mein Leben verändert! 8kg in 3 Monaten verloren.",
      rating: 5
    },
    {
      name: "Lisa K.",
      text: "Die personalisierten Pläne passen perfekt in meinen Alltag.",
      rating: 5
    },
    {
      name: "Anna W.",
      text: "Endlich ein Programm, das wirklich funktioniert!",
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-zinc-950/80 backdrop-blur-md border-b border-zinc-800/50">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <Dumbbell className="w-6 h-6 text-purple-500" />
              <span className="text-xl font-bold text-white">ShapeGirls</span>
            </Link>

            <nav className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-zinc-400 hover:text-white transition-colors">Features</a>
              <a href="#testimonials" className="text-zinc-400 hover:text-white transition-colors">Erfolge</a>
              <Button 
                onClick={() => navigate('/quiz')}
                className="h-10 px-6 rounded-full bg-purple-600 hover:bg-purple-500 text-white font-medium">
                Starten
              </Button>
            </nav>

            <button 
              className="md:hidden text-white p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <motion.div 
            className="md:hidden bg-zinc-900 border-t border-zinc-800"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="container mx-auto px-4 py-4 flex flex-col gap-4">
              <a href="#features" className="text-zinc-400 hover:text-white transition-colors py-2" onClick={() => setMobileMenuOpen(false)}>Features</a>
              <a href="#testimonials" className="text-zinc-400 hover:text-white transition-colors py-2" onClick={() => setMobileMenuOpen(false)}>Erfolge</a>
              <Button 
                onClick={() => { navigate('/quiz'); setMobileMenuOpen(false); }}
                className="h-10 rounded-full bg-purple-600 hover:bg-purple-500 text-white font-medium">
                Starten
              </Button>
            </div>
          </motion.div>
        )}
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-900/20 via-zinc-950 to-zinc-950" />
        
        {/* Decorative Elements */}
        <div className="absolute top-20 right-20 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl" />

        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <motion.div 
              className="lg:col-span-7 space-y-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/10 border border-purple-500/20 rounded-full">
                <Sparkles className="w-4 h-4 text-purple-400" />
                <span className="text-sm text-purple-400 font-medium tracking-wide">DEINE FITNESS REISE BEGINNT HIER</span>
              </div>

              <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-none">
                Erreiche deine
                <span className="block bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-teal-400">
                  Traumfigur
                </span>
              </h1>

              <p className="text-lg leading-relaxed text-zinc-400 max-w-2xl">
                Personalisierte Trainings- und Ernährungspläne, die sich deinem Leben anpassen. 
                Starte noch heute deine Transformation mit ShapeGirls.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  data-testid="start-program-btn"
                  onClick={() => navigate('/quiz')}
                  className="h-12 px-8 rounded-full bg-purple-600 hover:bg-purple-500 text-white font-semibold transition-all hover:scale-105 active:scale-95 shadow-[0_0_20px_rgba(147,51,234,0.3)]">
                  Programm starten
                  <Zap className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  variant="outline"
                  className="h-12 px-8 rounded-full bg-zinc-800 hover:bg-zinc-700 text-white font-medium border border-zinc-700">
                  Mehr erfahren
                </Button>
              </div>

              <div className="flex items-center gap-8 pt-4">
                <div>
                  <div className="text-3xl font-bold text-white">100k+</div>
                  <div className="text-sm text-zinc-400">Aktive Nutzerinnen</div>
                </div>
                <div className="w-px h-12 bg-zinc-800" />
                <div>
                  <div className="text-3xl font-bold text-white">4.9</div>
                  <div className="text-sm text-zinc-400">Bewertung</div>
                </div>
                <div className="w-px h-12 bg-zinc-800" />
                <div>
                  <div className="text-3xl font-bold text-white">1M+</div>
                  <div className="text-sm text-zinc-400">Workouts</div>
                </div>
              </div>
            </motion.div>

            <motion.div 
              className="lg:col-span-5"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="relative aspect-square rounded-2xl overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1686561394788-e450f15ff1e5?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Strong woman in dark studio lighting"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-transparent to-transparent" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 md:py-32 relative">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-4">Was dich erwartet</h2>
            <p className="text-base leading-relaxed text-zinc-400 max-w-2xl mx-auto">
              Alles, was du brauchst, um deine Fitness-Ziele zu erreichen
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="bg-zinc-900/50 backdrop-blur-md border border-zinc-800/50 rounded-2xl p-6 hover:border-purple-500/30 transition-all duration-300 group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="w-14 h-14 rounded-xl bg-purple-500/10 flex items-center justify-center mb-4 text-purple-400 group-hover:scale-110 transition-transform">
                  {feature.icon}
                </div>
                <h3 className="text-2xl md:text-3xl font-medium mb-3">{feature.title}</h3>
                <p className="text-base leading-relaxed text-zinc-400">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 md:py-32 bg-zinc-900/30">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-4">Erfolgsgeschichten</h2>
            <p className="text-base leading-relaxed text-zinc-400 max-w-2xl mx-auto">
              Tausende Frauen haben bereits ihre Ziele erreicht
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                className="bg-zinc-900/50 backdrop-blur-md border border-zinc-800/50 rounded-2xl p-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Heart key={i} className="w-5 h-5 fill-purple-500 text-purple-500" />
                  ))}
                </div>
                <p className="text-base text-zinc-300 mb-4">"{testimonial.text}"</p>
                <p className="font-medium text-purple-400">{testimonial.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-900/10 to-transparent" />
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          <motion.div 
            className="text-center space-y-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight">
              Bereit für deine
              <span className="block bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-teal-400">
                Transformation?
              </span>
            </h2>
            <p className="text-base leading-relaxed text-zinc-400 max-w-2xl mx-auto">
              Starte noch heute mit unserem personalisierten Programm
            </p>
            <Button 
              data-testid="cta-start-btn"
              onClick={() => navigate('/quiz')}
              className="h-12 px-8 rounded-full bg-purple-600 hover:bg-purple-500 text-white font-semibold transition-all hover:scale-105 active:scale-95 shadow-[0_0_20px_rgba(147,51,234,0.3)]">
              Jetzt starten
              <Zap className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-zinc-800">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-zinc-500">© 2025 ShapeGirls. Alle Rechte vorbehalten.</p>
            <nav className="flex items-center gap-6">
              <Link to="/impressum" className="text-zinc-500 hover:text-zinc-300 transition-colors">Impressum</Link>
              <Link to="/agb" className="text-zinc-500 hover:text-zinc-300 transition-colors">AGB</Link>
              <Link to="/datenschutz" className="text-zinc-500 hover:text-zinc-300 transition-colors">Datenschutz</Link>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;